import React, {Component, ReactElement, ReactNode, RefObject} from 'react'
import styled from 'styled-components'
import {Box, IconButton, OutlinedInput} from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import {FlexContainer, LeftRowFlexContainer, RowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {HKConstants} from '../../../../HKConstants'
import {UnstyledLinkStyledButton} from '../../../../styledComponents/Button'
import {Colors, FontWeights} from '../../../../styledComponents/Constants'
import NatAdminDisasterCommentError, {InputError} from './NatAdminDisasterCommentError'
import {ImgAddIcon} from '../../../common/icons/NatSvgIcon'
import {UserIconLarge} from '../../../../components/assocDirector/AssocDirectorCommon'
import {Nullable} from '../../../../app/TypeHelpers'
import {
    AdminLoginInfoSessionStorageKey,
    NatAdminLoginInfoSessionStorageHelper
} from '../../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import {Comment} from '../repository/NatGetDisasterCommentForAdmin'

export interface DisasterCommentFile {
    fileNumber: number | null
    fileName: string
    fileData: string | null
    fileSize: number
}

interface Props {
    type: string
    replySourceId?: number
    commentInfo?: Comment
    submitButtonLabel: string
    clearButtonLabel: string
    onClickSubmitButton?: () => Promise<void>
    onClickEditButton?: (
        commentInfo: Comment
    ) => void
    onClickCancelButton?: () => void
    onChange: (
        comment: string,
        commentFileList: DisasterCommentFile[],
        deleteFileNumberList: number[],
        inputError: Nullable<InputError>
    ) => void
    inputtingComment: string
    inputtingCommentFileList: DisasterCommentFile[]
    deleteFileNumberList: number[]
    inputError: Nullable<InputError>
}

class NatAdminDisasterCommentInputForm extends Component<Props> {
    commentFileRef: RefObject<HTMLInputElement>
    fileUploadId: string;

    constructor(props: Readonly<Props>) {
        super(props)

        this.fileUploadId = props.type + (
            props.type === 'reply-'
                ? props.replySourceId
                : props.commentInfo?.commentId ?? '')

        this.commentFileRef = React.createRef()
        this.handleChangeComment = this.handleChangeComment.bind(this)
        this.handleClickAddFileButton = this.handleClickAddFileButton.bind(this)
        this.handleChangeFileInput = this.handleChangeFileInput.bind(this)
        this.handleClickDeleteFileButton = this.handleClickDeleteFileButton.bind(this)
    }

    handleChangeComment(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.props.onChange(
            event.target.value,
            this.props.inputtingCommentFileList,
            this.props.deleteFileNumberList,
            null
        )
    }

    onClickEditComment(commentInfo: Comment): void {
        this.props.onClickEditButton?.(
            commentInfo
        )
    }

    handleClickAddFileButton(): void {
        const fileUpload = document.getElementById('fileUpload-' + this.fileUploadId) as HTMLInputElement
        if (fileUpload) {
            this.props.onChange(
                this.props.inputtingComment,
                this.props.inputtingCommentFileList,
                this.props.deleteFileNumberList,
                null
            )
            fileUpload.click()
        }
    }

    handleChangeFileInput(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files === null ||
            event.target.files.length === 0 ||
            event.target.files[0] === null) {
            return
        }

        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const {inputtingCommentFileList} = this.props
            const fileName = event.target.files[0].name
            const fileSize = event.target.files[0].size
            const extension = fileName.split('.').pop()?.toLowerCase()
            const allowedExtensions = ['jpg', 'jpeg', 'png']

            let inputError: InputError | undefined
            if (fileSize > HKConstants.CMN.DIS.MAX_FILE_SIZE * 1024 * 1024) {
                inputError = {
                    title: '画像を追加できませんでした。',
                    messages: ['アップロードの上限サイズ(20MB)を超えています。画像のサイズを確認してください。']
                }
            }

            if (!extension || !allowedExtensions.includes(extension)) {
                inputError = {
                    title: '画像を追加できませんでした。',
                    messages: ['jpeg,jpg,png,heif,heicファイルを選択してください。']
                }
            }

            if (inputError) {
                this.props.onChange(
                    this.props.inputtingComment,
                    inputtingCommentFileList,
                    this.props.deleteFileNumberList,
                    inputError
                )
                if (this.commentFileRef.current) {
                    this.commentFileRef.current.value = ''
                }
                return
            }

            const reader: FileReader = new FileReader()
            reader.onload = () => {
                inputtingCommentFileList.push({
                    fileNumber: null,
                    fileName: fileName,
                    fileSize: fileSize,
                    fileData: String(reader.result)
                })
                this.props.onChange(
                    this.props.inputtingComment,
                    inputtingCommentFileList,
                    this.props.deleteFileNumberList,
                    this.props.inputError
                )
            }
            reader.onerror = () => {
                this.props.onChange(
                    this.props.inputtingComment,
                    inputtingCommentFileList,
                    this.props.deleteFileNumberList,
                    {
                        title: '画像を追加できませんでした。',
                        messages: ['画像の読み込みに失敗しました。もう一度画像を選択してください。'],
                        fileNameList: [`${fileName}`]
                    },
                )
            }
            reader.readAsDataURL(event.target.files[0])
        }
        if (this.commentFileRef.current) {
            this.commentFileRef.current.value = ''
        }
    }

    handleClickDeleteFileButton(deleteIndex: number): void {
        const {inputtingCommentFileList, deleteFileNumberList} = this.props
        const newCommentFileList: DisasterCommentFile[] = []
        const newDeleteFileNumberList: number[] = [...deleteFileNumberList]

        for (let index = 0; index < inputtingCommentFileList.length; index++) {
            const commentFile = inputtingCommentFileList[index]
            if (index !== deleteIndex) {
                newCommentFileList.push(commentFile)
            } else if (commentFile.fileNumber) {
                newDeleteFileNumberList.push(commentFile.fileNumber)
            }
        }

        this.props.onChange(
            this.props.inputtingComment,
            newCommentFileList,
            newDeleteFileNumberList,
            this.props.inputError
        )
    }

    renderNewCommentHeader(): ReactElement {
        const loginFamilyName = NatAdminLoginInfoSessionStorageHelper
            .getStringItemOrNull(AdminLoginInfoSessionStorageKey.LOGIN_USER_FAMILY_NAME)
        const loginFirstName = NatAdminLoginInfoSessionStorageHelper
            .getStringItemOrNull(AdminLoginInfoSessionStorageKey.LOGIN_USER_FIRST_NAME)
        const loginRoleName = NatAdminLoginInfoSessionStorageHelper
            .getStringItemOrNull(AdminLoginInfoSessionStorageKey.LOGIN_USER_ROLE_NAME)
        const posterName = loginFamilyName + ' ' + loginFirstName + '（' + loginRoleName + '）'

        return (
            <RowFlexContainer>
                <LeftRowFlexContainer>
                    <UserIconLarge/>
                    <Box width={'15px'}/>
                    <UserNameDiv>{posterName}</UserNameDiv>
                </LeftRowFlexContainer>
            </RowFlexContainer>
        )
    }

    render(): ReactNode {
        const {
            commentInfo, inputtingComment, inputtingCommentFileList, submitButtonLabel,
            onClickSubmitButton, onClickCancelButton, inputError
        } = this.props

        const disabledAddFileButton = inputtingCommentFileList.length >= 5
        const isSubmitDisabled = inputtingComment.length === 0

        return <>
            {!commentInfo && this.renderNewCommentHeader()}
            <Box mt={'10px'} ml={!commentInfo ? '47px' : 0}>
                <StyledFlexContainer>
                    <InputContainer>
                        {
                            inputError &&
                            <NatAdminDisasterCommentError inputError={inputError}/>
                        }
                        <PlainInput
                            inputProps={{
                                minLength: 0,
                                maxLength: 2000,
                            }}
                            maxRows={4.5}
                            value={inputtingComment}
                            fullWidth
                            multiline
                            autoFocus
                            onChange={this.handleChangeComment}
                        />
                        {inputtingCommentFileList.length !== 0 && <Box display={'flex'} style={{gap: '5px'}} mt={'5px'}>
                            {inputtingCommentFileList.map((commentFile, index) => {
                                return (
                                    <ThumbnailContainer key={index}>
                                        <ThumbnailImg src={String(commentFile.fileData)}/>
                                        <VisibilityIconButton onClick={() => this.handleClickDeleteFileButton(index)}>
                                            <StyledNotInterestedIcon src={`/resources/images/icons/cancel.svg`} alt=""/>
                                        </VisibilityIconButton>
                                    </ThumbnailContainer>
                                )
                            })}
                        </Box>}
                    </InputContainer>
                    <Box height={'1px'} bgcolor={'#0070B0'}/>
                    <Box height={'6px'}/>
                    <RowFlexContainer>
                        <AddFileButton
                            id={'addFileButton'}
                            onClick={this.handleClickAddFileButton}
                            disabled={disabledAddFileButton}
                        >
                            <LeftRowFlexContainer>
                                <ImgAddIcon
                                    color={disabledAddFileButton ? Colors.AccentLight : Colors.AccentDark}
                                />
                            </LeftRowFlexContainer>
                        </AddFileButton>
                        <DisplayNoneFileInput
                            type={'file'}
                            id={'fileUpload-' + this.fileUploadId}
                            ref={this.commentFileRef}
                            onChange={this.handleChangeFileInput}
                        />
                        <RowFlexContainer>
                            {
                                onClickCancelButton &&
                                <>
                                    <StyledLinkButton onClick={onClickCancelButton}>
                                        キャンセル
                                    </StyledLinkButton>
                                    <Box width={'26px'}/>
                                </>
                            }
                            <SubmitButton
                                onClick={commentInfo ? () => this.onClickEditComment(commentInfo) : onClickSubmitButton}
                                disabled={isSubmitDisabled}
                            >
                                {submitButtonLabel}
                            </SubmitButton>
                        </RowFlexContainer>
                    </RowFlexContainer>
                    <Box height={'10px'}/>
                </StyledFlexContainer>
            </Box>
        </>
    }
}

export default NatAdminDisasterCommentInputForm

const DisplayNoneFileInput = styled.input`
    display: none;
`

const ThumbnailContainer = styled(Box)`
    display: flex;
    position: relative;
    flex-wrap: wrap;
    width: calc((100% - 20px) / 5);
    max-width: 60px;
    height: 60px;
`

const ThumbnailImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: ${Colors.AccentLight};
`

const VisibilityIconButton = withStyles({
    root: {
        padding: 0,
        color: '#00000066',
        position: 'absolute',
        left: '46px',
        top: '2px',
        '& path': {
            transform: 'scale(1.2)',
            transformOrigin: 'center'
        }
    }
})(IconButton)

const StyledNotInterestedIcon = styled.img`
    width: 12px;
    height: 12px;
`

const StyledFlexContainer = styled(FlexContainer)`
    flex-direction: column;
    justify-content: flex-start;
    height: 100%;
    width: 100%;
`

const AddFileButton = styled(UnstyledLinkStyledButton)`
    color: ${Colors.AccentDark};

    :disabled {
        cursor: default;
        color: ${Colors.AccentLight};
    }
`

const InputContainer = styled.label`
    background-color: #F2F8FB;
    padding: 10px 15px;
    border-bottom: 1px solid #F2F8FB;

    &:focus-within {
        border-bottom-color: #0070B0;
    }
`

const PlainInput = withStyles({
    root: {
        background: 'none',
        fontSize: '14px',
        padding: 0,
    },
    input: {
        lineHeight: 1.5,
    },
    notchedOutline: {
        border: 'none',
    },
})
(OutlinedInput)

const StyledLinkButton = styled.button`
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;

    &:focus {
        outline: 0;
    }

    color: ${Colors.AccentDark};

    :disabled {
        color: ${Colors.AccentLight};
    }
`

const SubmitButton = styled(StyledLinkButton)`
    color: ${Colors.Primary};
`

const UserNameDiv = styled.div`
    font-weight: ${FontWeights.Bold};
    font-size: 14px;
`